import i18n from 'i18n-js';
import { ChangeSummary, ChangeSummaryEntry, ChangeType, DiffListItem, NCDialect, ProcessedLine } from '@components/ncFileVersions/components/NCFileDiffView/types';
import {
  CATEGORY_DESCRIPTORS_BY_DIALECT,
  ChangeSummaryCategory,
  COMMENT_LABEL_MAX_LENGTH,
  COORDINATE_DELTA_PRECISION,
  COORDINATE_TOKEN_REGEX,
} from '../constants/classifyChanges.constants';

const emptyCounts = (): Record<ChangeSummaryCategory, number> => ({
  [ChangeSummaryCategory.TOOL]: 0,
  [ChangeSummaryCategory.M_CODE]: 0,
  [ChangeSummaryCategory.FEED_RATE]: 0,
  [ChangeSummaryCategory.SPINDLE_SPEED]: 0,
  [ChangeSummaryCategory.RADIUS_COMP]: 0,
  [ChangeSummaryCategory.COORDINATE]: 0,
  [ChangeSummaryCategory.POLAR]: 0,
  [ChangeSummaryCategory.CYCLE_CODE]: 0,
  [ChangeSummaryCategory.PARAMETER]: 0,
  [ChangeSummaryCategory.COMMENT]: 0,
});

const extractTokens = (text: string, pattern: RegExp): string[] => {
  if (!text) {
    return [];
  }
  return text.match(pattern) ?? [];
};

const truncate = (text: string, max: number): string => (text.length <= max ? text : `${text.slice(0, Math.max(0, max - 1))}…`);

const formatSignedDelta = (delta: number): string => {
  if (delta === 0) {
    return '0';
  }
  const fixed = delta.toFixed(COORDINATE_DELTA_PRECISION);
  return delta > 0 ? `+${fixed}` : fixed;
};

const buildCoordinateLabel = (before: string, after: string): string | null => {
  const beforeMatch = before.match(COORDINATE_TOKEN_REGEX);
  const afterMatch = after.match(COORDINATE_TOKEN_REGEX);
  if (!beforeMatch || !afterMatch || beforeMatch[1] !== afterMatch[1]) {
    return null;
  }
  const beforeValue = Number(beforeMatch[2]);
  const afterValue = Number(afterMatch[2]);
  if (!Number.isFinite(beforeValue) || !Number.isFinite(afterValue)) {
    return null;
  }
  return `${beforeMatch[1]} ${formatSignedDelta(afterValue - beforeValue)}`;
};

const buildLabel = (category: ChangeSummaryCategory, before: string | undefined, after: string | undefined): string => {
  const isComment = category === ChangeSummaryCategory.COMMENT;
  const cap = (text: string) => (isComment ? truncate(text, COMMENT_LABEL_MAX_LENGTH) : text);

  if (before && after) {
    if (category === ChangeSummaryCategory.COORDINATE) {
      const deltaLabel = buildCoordinateLabel(before, after);
      if (deltaLabel) {
        return deltaLabel;
      }
    }
    return `${cap(before)} → ${cap(after)}`;
  }
  if (after) {
    return `${cap(after)} ${i18n.t('added')}`;
  }
  if (before) {
    return `${cap(before)} ${i18n.t('removed')}`;
  }
  return '';
};

const isChangedLine = (line: ProcessedLine | null | undefined): boolean =>
  Boolean(line) && (line!.changeType === ChangeType.ADDED || line!.changeType === ChangeType.REMOVED);

const sameMultiset = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  if (a.length === 0) {
    return true;
  }
  const counts: Record<string, number> = {};
  a.forEach((token) => (counts[token] = (counts[token] ?? 0) + 1));
  return b.every((token) => counts[token] && counts[token]-- > 0);
};

// Cancel identical tokens between a and b, return each side's remainder in
// original order so downstream positional pairing stays deterministic.
const diffMultisets = (a: string[], b: string[]): { remainA: string[]; remainB: string[] } => {
  if (a.length === 0) {
    return { remainA: [], remainB: b.slice() };
  }
  if (b.length === 0) {
    return { remainA: a.slice(), remainB: [] };
  }
  const counts = new Map<string, number>();
  for (const token of a) {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  const remainB: string[] = [];
  for (const token of b) {
    const c = counts.get(token) ?? 0;
    if (c > 0) {
      counts.set(token, c - 1);
    } else {
      remainB.push(token);
    }
  }
  // `counts` now holds the residual of `a` after b-cancellations.
  // Walk `a` again, emitting only the items still backed by that residual.
  const remainA: string[] = [];
  for (const token of a) {
    const c = counts.get(token) ?? 0;
    if (c > 0) {
      counts.set(token, c - 1);
      remainA.push(token);
    }
  }
  return { remainA, remainB };
};

// Pairing happens inside a bucket, so the key picks what counts as "the same
// slot" across the two sides: axis letter for coordinates (X pairs with X,
// never Y/Z), parameter name for Q-/#-params (Q1 pairs with Q1), single bucket
// for everything else.
type TokenKeyFn = (token: string) => string;

const DEFAULT_KEY: TokenKeyFn = () => '*';
const COORDINATE_AXIS_PATTERN = /^([XYZ])/i;
const PARAMETER_NAME_PATTERN = /^([Q#](?:\d+|<[^>]+>))/;

const getCategoryKeyFn = (category: ChangeSummaryCategory): TokenKeyFn => {
  if (category === ChangeSummaryCategory.COORDINATE) {
    return (token) => token.match(COORDINATE_AXIS_PATTERN)?.[1].toUpperCase() ?? '*';
  }
  if (category === ChangeSummaryCategory.PARAMETER) {
    return (token) => token.match(PARAMETER_NAME_PATTERN)?.[1] ?? '*';
  }
  return DEFAULT_KEY;
};

const bucketByKey = (tokens: string[], keyFn: TokenKeyFn): Map<string, string[]> => {
  const buckets = new Map<string, string[]>();
  for (const token of tokens) {
    const key = keyFn(token);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(token);
    } else {
      buckets.set(key, [token]);
    }
  }
  return buckets;
};

export const classifyChanges = (preparedListItems: DiffListItem[], dialect: NCDialect): ChangeSummary => {
  const entries: ChangeSummaryEntry[] = [];
  const countsByCategory = emptyCounts();
  const descriptors = CATEGORY_DESCRIPTORS_BY_DIALECT[dialect];

  for (const item of preparedListItems) {
    if (item.type !== 'row') {
      continue;
    }
    const oldChanged = isChangedLine(item.oldLine);
    const newChanged = isChangedLine(item.newLine);
    if (!oldChanged && !newChanged) {
      continue;
    }

    const oldContent = item.oldLine?.content ?? '';
    const newContent = item.newLine?.content ?? '';

    for (const { category, pattern } of descriptors) {
      const oldTokens = oldChanged ? extractTokens(oldContent, pattern) : [];
      const newTokens = newChanged ? extractTokens(newContent, pattern) : [];

      if (oldTokens.length === 0 && newTokens.length === 0) {
        continue;
      }
      if (sameMultiset(oldTokens, newTokens)) {
        continue;
      }

      const keyFn = getCategoryKeyFn(category);
      const oldBuckets = bucketByKey(oldTokens, keyFn);
      const newBuckets = bucketByKey(newTokens, keyFn);
      // Map.keys() iterates in insertion order, so first-seen-on-old then
      // first-seen-on-new falls out of the spread — no extra ordering pass.
      const orderedKeys = new Set<string>([...oldBuckets.keys(), ...newBuckets.keys()]);

      let pairIndex = 0;
      for (const key of orderedKeys) {
        const { remainA: remainOld, remainB: remainNew } = diffMultisets(
          oldBuckets.get(key) ?? [],
          newBuckets.get(key) ?? [],
        );
        const pairCount = Math.max(remainOld.length, remainNew.length);
        for (let i = 0; i < pairCount; i++) {
          const before = remainOld[i];
          const after = remainNew[i];
          if (before && after && before === after) {
            continue;
          }
          const label = buildLabel(category, before, after);
          if (!label) {
            continue;
          }
          countsByCategory[category] += 1;
          entries.push({
            id: `${item.key}-${category}-${pairIndex}`,
            category,
            label,
            before,
            after,
            rowIndex: item.index,
            rowKey: item.key,
          });
          pairIndex++;
        }
      }
    }
  }

  return { entries, countsByCategory, totalChanges: entries.length };
};
